import { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  HiOutlineArchiveBox,
  HiOutlineArrowPath,
  HiOutlineMagnifyingGlass,
  HiOutlinePencilSquare,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineUserGroup,
} from "react-icons/hi2";

import PageHeader from "../../../components/ui/pageHeader/PageHeader";
import PageCard from "../../../components/ui/pageCard/PageCard";
import PageLoader from "../../../components/ui/pageLoader/PageLoader";
import PageEmpty from "../../../components/ui/pageEmpty/PageEmpty";
import Modal from "../../../components/ui/modal/Modal";
import ConfirmModal from "../../../components/ui/confirmModal/ConfirmModal";

import ClientForm from "../../../components/forms/client/ClientForm";

import usePermissions from "../../../hooks/usePermissions";

import {
  archiveClient,
  deleteClient,
  getClients,
  restoreClient,
  setClientFilters,
  setClientPage,
} from "../../../redux/features/clients/clientSlice";

import "./ClientsPage.css";

export default function ClientsPage() {
  const dispatch = useDispatch();
  const { can } = usePermissions();

  const { clients, pagination, filters, isLoading, isDeleting, error } =
    useSelector((state) => state.clients);

  const [searchValue, setSearchValue] = useState(filters.search || "");

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedEditClient, setSelectedEditClient] = useState(null);

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Bevestigen",
    variant: "danger",
    onConfirm: null,
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(
        setClientFilters({
          search: searchValue,
        }),
      );
    }, 400);

    return () => clearTimeout(timeout);
  }, [dispatch, searchValue]);

  useEffect(() => {
    dispatch(
      getClients({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      }),
    );
  }, [dispatch, pagination.page, pagination.limit, filters]);

  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      title: "",
      message: "",
      confirmText: "Bevestigen",
      variant: "danger",
      onConfirm: null,
    });
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    if (name === "search") {
      setSearchValue(value);
      return;
    }

    dispatch(
      setClientFilters({
        [name]: value,
      }),
    );
  };

  const openEdit = (client) => {
    setSelectedEditClient(client);
    setOpenEditModal(true);
  };

  const getClientName = (client) => {
    return `${client.first_name || ""} ${client.last_name || ""}`.trim();
  };

  const openArchiveConfirm = (client) => {
    setConfirmModal({
      isOpen: true,
      title: "Jongere archiveren?",
      message: `${getClientName(client)} wordt gearchiveerd.`,
      confirmText: "Archiveren",
      variant: "danger",
      onConfirm: async () => {
        await dispatch(archiveClient(client._id)).unwrap();
        closeConfirmModal();
      },
    });
  };

  const openRestoreConfirm = (client) => {
    setConfirmModal({
      isOpen: true,
      title: "Cliënt herstellen?",
      message: `${getClientName(client)} wordt opnieuw actief.`,
      confirmText: "Herstellen",
      variant: "success",
      onConfirm: async () => {
        await dispatch(restoreClient(client._id)).unwrap();
        closeConfirmModal();
      },
    });
  };

  const openDeleteConfirm = (client) => {
    setConfirmModal({
      isOpen: true,
      title: "Cliënt permanent verwijderen?",
      message: `Deze actie verwijdert ook alle trajecten van deze cliënt. Deze actie kan niet ongedaan gemaakt worden.`,
      confirmText: "Permanent verwijderen",
      variant: "danger",
      onConfirm: async () => {
        await dispatch(deleteClient(client._id)).unwrap();
        closeConfirmModal();
      },
    });
  };

  if (isLoading && clients.length === 0) {
    return <PageLoader title="Jongeren laden..." />;
  }

  return (
    <div className="clients-page">
      <PageHeader
        eyebrow="POWER"
        title="Jongeren"
        description="Beheer jongeren die gekoppeld kunnen worden aan trajecten."
        actions={
          can("client.create") && (
            <button
              className="clients-page__create-button"
              onClick={() => setOpenCreateModal(true)}
            >
              <HiOutlinePlus />
              <span>Nieuwe jongere</span>
            </button>
          )
        }
      />

      {error && <div className="clients-page__error">{error}</div>}

      <PageCard className="clients-page__filters-card">
        <div className="clients-page__filters">
          <div className="clients-page__search">
            <HiOutlineMagnifyingGlass />

            <input
              type="text"
              name="search"
              value={searchValue}
              onChange={handleFilterChange}
              placeholder="Zoeken op naam..."
              autoComplete="off"
            />
          </div>

          <select
            name="is_active"
            value={filters.is_active || ""}
            onChange={handleFilterChange}
          >
            <option value="">Alle statussen</option>
            <option value="true">Actief</option>
            <option value="false">Gearchiveerd</option>
          </select>
        </div>
      </PageCard>

      {isLoading && clients.length > 0 && (
        <div className="clients-page__small-loading">Jongeren bijwerken...</div>
      )}

      {clients.length === 0 ? (
        <PageEmpty
          icon={<HiOutlineUserGroup />}
          title="Geen jongeren gevonden"
          description="Er zijn nog geen jongeren of de filters geven geen resultaat."
        />
      ) : (
        <PageCard className="clients-page__table-card">
          <div className="clients-page__table-wrapper">
            <table className="clients-page__table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Naam</th>
                  <th>Status</th>
                  <th>Aangemaakt</th>
                  <th>Acties</th>
                </tr>
              </thead>

              <tbody>
                {clients.map((client) => (
                  <tr key={client._id}>
                    <td>{client._id}</td>

                    <td>
                      <div className="clients-page__client">
                        <strong>{getClientName(client)}</strong>
                      </div>
                    </td>

                    <td>
                      <span
                        className={`clients-page__status ${
                          client.is_active
                            ? "clients-page__status--active"
                            : "clients-page__status--archived"
                        }`}
                      >
                        {client.is_active ? "Actief" : "Gearchiveerd"}
                      </span>
                    </td>

                    <td>
                      {client.createdAt
                        ? new Date(client.createdAt).toLocaleDateString("nl-BE")
                        : "-"}
                    </td>

                    <td>
                      <div className="clients-page__actions">
                        {can("client.update") && (
                          <button
                            type="button"
                            onClick={() => openEdit(client)}
                            title="Wijzigen"
                          >
                            <HiOutlinePencilSquare />
                          </button>
                        )}

                        {can("client.update") && client.is_active && (
                          <button
                            type="button"
                            className="clients-page__archive"
                            onClick={() => openArchiveConfirm(client)}
                            title="Archiveren"
                          >
                            <HiOutlineArchiveBox />
                          </button>
                        )}

                        {can("client.update") && !client.is_active && (
                          <button
                            type="button"
                            className="clients-page__restore"
                            onClick={() => openRestoreConfirm(client)}
                            title="Herstellen"
                          >
                            <HiOutlineArrowPath />
                          </button>
                        )}

                        {can("client.delete") && (
                          <button
                            type="button"
                            className="clients-page__delete"
                            onClick={() => openDeleteConfirm(client)}
                            disabled={isDeleting}
                            title="Permanent verwijderen"
                          >
                            <HiOutlineTrash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </PageCard>
      )}

      {pagination.pages > 1 && (
        <div className="clients-page__pagination">
          <button
            disabled={pagination.page <= 1}
            onClick={() => dispatch(setClientPage(pagination.page - 1))}
          >
            Vorige
          </button>

          <span>
            Pagina {pagination.page} van {pagination.pages}
          </span>

          <button
            disabled={pagination.page >= pagination.pages}
            onClick={() => dispatch(setClientPage(pagination.page + 1))}
          >
            Volgende
          </button>
        </div>
      )}

      <Modal
        isOpen={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        title="Nieuwe cliënt"
        description="Maak een nieuwe cliënt aan."
      >
        <ClientForm
          mode="create"
          onSuccess={() => setOpenCreateModal(false)}
          onCancel={() => setOpenCreateModal(false)}
        />
      </Modal>

      <Modal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
        title="Cliënt wijzigen"
        description="Wijzig de gegevens van deze cliënt."
      >
        <ClientForm
          mode="edit"
          initialData={selectedEditClient}
          onSuccess={() => setOpenEditModal(false)}
          onCancel={() => setOpenEditModal(false)}
        />
      </Modal>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={isDeleting ? "Verwijderen..." : confirmModal.confirmText}
        variant={confirmModal.variant}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
      />
    </div>
  );
}
